import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import NextLink from 'next/link'
import { Image } from 'components/Image'
import { Button } from '@achieve/ascend'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import useIsSsr from 'hooks/useIsSsr'
import Cookies from 'js-cookie'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { FdrPage } from 'utils/environmentConfig'
import { PRD_ENV } from 'constants/environments'

const CONSENT_BANNER_COOKIE = 'achieve_tracking_consent_banner'

const bannerCopy = [
  'We use and allow cookies and similar tools on our websites to enable essential functionalities and to better understand how visitors use our site (including use of session replay technology), as well as for personalization, social media features, and advertising. Read our',
  'privacy policy',
  'to learn more. By closing this box or by your continued use of our sites, you accept such use.',
]

const ConsentBanner = function ({ contentfulId, contentfulVersion, siteEnv, publicProcessDotEnv }) {
  const {
    state: { isPageLoaded },
  } = useContext(AnalyticsContext)

  const isSsr = useIsSsr()
  const { fireAndForget } = useTriggerEvents()
  const [menuCondensed] = useLayoutMenuCondensed()

  const [acknowledgedState, setAcknowledgedState] = useState(false)
  const consentBannerCookie = Cookies.get(CONSENT_BANNER_COOKIE)
  const isConsentBannerAcknowledged = !!consentBannerCookie
  const bannerVisible = !isSsr && !isConsentBannerAcknowledged && !acknowledgedState

  const onClick = useCallback(() => {
    const visit_id = Cookies.get('eh_visit_id')
    const cookieDomain =
      publicProcessDotEnv?.NEXT_PUBLIC_APP_ENV === PRD_ENV ? '.achieve.com' : '.ffngcp.com'

    Cookies.set(
      CONSENT_BANNER_COOKIE,
      JSON.stringify({
        visitId: visit_id,
        contentfulId,
        contentfulVersion,
      }),
      {
        expires: 3650,
        domain: cookieDomain,
      }
    )

    setAcknowledgedState(true)
  }, [contentfulId, contentfulVersion, publicProcessDotEnv?.NEXT_PUBLIC_APP_ENV])

  useEffect(() => {
    if (!isConsentBannerAcknowledged && isPageLoaded) {
      fireAndForget({
        event_type: 'app_event',
        event_action: 'app_event',
        nav_link_section: 'TCB banner',
        track_event: '_views/policy',
        disclosure_copy: `${bannerCopy[0]} ${bannerCopy[1]} ${bannerCopy[2]}`,
        ...handleTrackAndReactEvent(null, {}),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoaded, isConsentBannerAcknowledged])

  const bannerClasses = [
    'bg-neutral-0',
    'shadow-[0_6px_40px_-4px_rgb(16_24_40_/_11%),0_9px_12px_-3px_rgb(16_24_40_/_5%)]',
    'fixed',
    'flex',
    'max-w-[1440px]',
    '-translate-x-2/4',
    'w-full',
    'left-2/4',
    menuCondensed && siteEnv === FdrPage ? 'bottom-[108px] lg:bottom-0' : 'bottom-0',
    bannerVisible ? 'visible opacity-100 z-50' : 'invisible opacity-0 z-[-1]',
    'transition-opacity duration-300',
  ].join(' ')

  const Banner = useMemo(() => {
    if (!bannerVisible) return null

    return (
      <div className={bannerClasses} data-testid="tcb-banner">
        <div className="absolute right-3 top-3" data-testid="tcb-close-container">
          <TrackingWrapper
            track={{
              list_name: 'Close consent click event',
              nav_link_section: 'TCB banner',
              event_action: 'ui_click',
            }}
            onClick={onClick}
          >
            <Image
              src="/multi-site-assets/close.svg"
              alt="Close button"
              width={18}
              height={18}
              loading="lazy"
              decoding="async"
              aria-hidden={true}
              data-testid="tcb-x-icon"
            />
          </TrackingWrapper>
        </div>

        <div className="bodyXs lg:bodyMd text-justify pl-4 pr-11 md:px-11 py-2 md:py-8 w-full">
          <div className="float-right h-full -mr-8 flex items-end [shape-outside:inset(calc(100%_-_36px)_0_0)] ml-4 lg:mt-0 md-custom:mt-3">
            <TrackingWrapper
              track={{
                list_name: 'Tracking consent click event',
                nav_link_section: 'TCB banner',
                click_text: 'Ok',
                event_action: 'button_click',
              }}
              onClick={onClick}
            >
              <Button className="p-2" size="small" variant="contained" data-testid="tcb-ok-button">
                <div className="px-6 text-base md:text-lg font-normal">Ok</div>
              </Button>
            </TrackingWrapper>
          </div>
          <div className="mb-[6px]">
            {bannerCopy[0]}&nbsp;
            <TrackingWrapper
              track={{
                list_name: 'Policies link click event',
                nav_link_section: 'TCB banner',
                click_text: 'Privacy policy',
                event_action: 'link_click',
              }}
            >
              <NextLink href="/policies#Privacy-Policy" target="_blank">
                {bannerCopy[1]}
              </NextLink>
            </TrackingWrapper>
            &nbsp;{bannerCopy[2]}
          </div>
        </div>
      </div>
    )
  }, [bannerVisible, bannerClasses, onClick])

  return <>{Banner}</>
}

export { ConsentBanner }
